import React, { useState, useEffect } from 'react';
import Editor from './Editor';
import { etiquetasHerramientasAPI } from '../services/api';
import { getClienteColor } from '../utils/clienteColors';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ClienteForm({ cliente, onClose, onSubmit }) {
  const [validationError, setValidationError] = useState(null);
  const [etiquetasHerramientas, setEtiquetasHerramientas] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    email: '',
    telefono: '',
    whatsapp: '',
    fecha_nacimiento: '',
    lugar_nacimiento: '',
    direccion: '',
    ciudad: '',
    provincia: '',
    pais: '',
    codigo_postal: '',
    nif_cif: '',
    nombre_empresa: '',
    objetivo: '',
    herramientas: '',
    aprendizaje: '',
    areas_mejora: '',
    modalidad_preferida: 'Presencial',
    bienvenida: '',
    tarjeta_cliente: '',
    consideraciones_especiales: '',
    consentimiento_comunicacion: false,
    consentimiento_datos: false,
    notas: '',
    activo: true,
    color: '',
  });

  useEffect(() => {
    etiquetasHerramientasAPI.getAll().then((r) => {
      setEtiquetasHerramientas(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setEtiquetasHerramientas([]));
  }, []);

  useEffect(() => {
    if (cliente) {
      setFormData({
        nombre: cliente.nombre || '',
        apellidos: cliente.apellidos || '',
        email: cliente.email || '',
        telefono: cliente.telefono || '',
        whatsapp: cliente.whatsapp || '',
        fecha_nacimiento: cliente.fecha_nacimiento ? cliente.fecha_nacimiento.split('T')[0] : '',
        lugar_nacimiento: cliente.lugar_nacimiento || '',
        direccion: cliente.direccion || '',
        ciudad: cliente.ciudad || '',
        provincia: cliente.provincia || '',
        pais: cliente.pais || '',
        codigo_postal: cliente.codigo_postal || '',
        nif_cif: cliente.nif_cif || '',
        nombre_empresa: cliente.nombre_empresa || '',
        objetivo: cliente.objetivo || '',
        herramientas: cliente.herramientas || '',
        aprendizaje: cliente.aprendizaje || '',
        areas_mejora: cliente.areas_mejora || '',
        modalidad_preferida: cliente.modalidad_preferida || 'Presencial',
        bienvenida: cliente.bienvenida || '',
        tarjeta_cliente: cliente.tarjeta_cliente || '',
        consideraciones_especiales: cliente.consideraciones_especiales || '',
        consentimiento_comunicacion: !!cliente.consentimiento_comunicacion,
        consentimiento_datos: !!cliente.consentimiento_datos,
        notas: cliente.notas || '',
        activo: cliente.activo !== undefined ? cliente.activo : true,
        color: cliente.color || '',
      });
    }
  }, [cliente]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Separador para herramientas (permite comas en nombres)
  const HERRAMIENTAS_SEPARATOR = ' || ';
  
  const parseHerramientasValue = (val) => {
    if (!val || typeof val !== 'string') return [];
    // Eliminar etiquetas HTML
    let stripped = val.replace(/<[^>]*>/g, ' ');
    // Decodificar entidades HTML comunes
    stripped = stripped
      .replace(/&nbsp;/g, ' ')
      .replace(/&aacute;/g, 'á')
      .replace(/&eacute;/g, 'é')
      .replace(/&iacute;/g, 'í')
      .replace(/&oacute;/g, 'ó')
      .replace(/&uacute;/g, 'ú')
      .replace(/&ntilde;/g, 'ñ')
      .replace(/&Aacute;/g, 'Á')
      .replace(/&Eacute;/g, 'É')
      .replace(/&Iacute;/g, 'Í')
      .replace(/&Oacute;/g, 'Ó')
      .replace(/&Uacute;/g, 'Ú')
      .replace(/&Ntilde;/g, 'Ñ')
      .replace(/&amp;/g, '&');
    // Normalizar espacios múltiples (pero preservar espacios simples)
    stripped = stripped.replace(/\s{2,}/g, ' ').trim();
    
    // Usar el nuevo separador si existe, sino fallback a coma (datos antiguos)
    if (stripped.includes('||')) {
      return stripped.split('||').map((s) => s.trim()).filter(Boolean);
    }
    // Fallback para datos antiguos con comas
    return stripped.split(',').map((s) => s.trim()).filter(Boolean);
  };

  const herramientasSelected = parseHerramientasValue(formData.herramientas);
  
  // Normalizar nombre para comparación (sin acentos ni espacios extra)
  const normalizeForComparison = (str) => {
    return str.trim().toLowerCase();
  };
  
  const isHerramientaSelected = (nombre) => {
    const normalized = normalizeForComparison(nombre);
    return herramientasSelected.some(h => normalizeForComparison(h) === normalized);
  };

  const toggleHerramientaTag = (nombre) => {
    // Buscar si ya está seleccionada (comparación normalizada)
    const yaSeleccionada = isHerramientaSelected(nombre);
    
    if (yaSeleccionada) {
      // Eliminar: filtrar las que NO coincidan con este nombre
      const nuevas = herramientasSelected.filter(h => 
        normalizeForComparison(h) !== normalizeForComparison(nombre)
      );
      setFormData((prev) => ({ ...prev, herramientas: nuevas.join(HERRAMIENTAS_SEPARATOR) }));
    } else {
      // Añadir
      const nuevas = [...herramientasSelected, nombre];
      setFormData((prev) => ({ ...prev, herramientas: nuevas.join(HERRAMIENTAS_SEPARATOR) }));
    }
  };

  const selectAllHerramientas = () => {
    const todas = etiquetasHerramientas.map((e) => e.nombre);
    setFormData((prev) => ({ ...prev, herramientas: todas.join(HERRAMIENTAS_SEPARATOR) }));
  };

  const clearAllHerramientas = () => {
    setFormData((prev) => ({ ...prev, herramientas: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationError(null);
    const nombre = (formData.nombre || '').trim();
    const apellidos = (formData.apellidos || '').trim();
    const email = (formData.email || '').trim();
    if (!nombre) {
      setValidationError('El nombre es obligatorio.');
      return;
    }
    if (!apellidos) {
      setValidationError('Los apellidos son obligatorios.');
      return;
    }
    if (email && !EMAIL_REGEX.test(email)) {
      setValidationError('Introduce una dirección de correo electrónico válida.');
      return;
    }
    onSubmit({ ...formData, nombre, apellidos, email: email || formData.email });
  };

  return (
    <div
      className="modal"
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', overflowY: 'auto', zIndex: 4000 }}
    >
      <div
        className="modal-content cliente-form-modal"
        style={{ position: 'relative', zIndex: 4001 }}
      >
        <div className="modal-header">
          <h2>{cliente ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        {validationError && (
          <div className="alert alert-error" style={{ margin: '0 20px 16px' }}>{validationError}</div>
        )}
        {cliente && (
          <div className="cliente-color-indicator" style={{ 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderBottom: '1px solid #dee2e6'
          }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: '10px' }}>Color en Calendario</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="color"
                name="color"
                value={formData.color || cliente.color || getClienteColor(cliente.id)}
                onChange={handleChange}
                style={{
                  width: '60px',
                  height: '40px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                name="color"
                value={formData.color || cliente.color || getClienteColor(cliente.id)}
                onChange={handleChange}
                placeholder="#000000"
                pattern="^#[0-9A-Fa-f]{6}$"
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    color: ''
                  }));
                }}
                style={{ padding: '8px 16px' }}
              >
                Usar color automático
              </button>
            </div>
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
              Selecciona un color personalizado o deja vacío para usar el color automático basado en el ID
            </small>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre *</label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Apellidos *</label>
            <input
              type="text"
              name="apellidos"
              value={formData.apellidos}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Teléfono</label>
            <input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>WhatsApp</label>
            <input
              type="tel"
              name="whatsapp"
              value={formData.whatsapp}
              onChange={handleChange}
              placeholder="Ej: 34612345678"
            />
          </div>
          <div className="form-group">
            <label>Fecha de Nacimiento</label>
            <input
              type="date"
              name="fecha_nacimiento"
              value={formData.fecha_nacimiento}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Lugar de Nacimiento</label>
            <input
              type="text"
              name="lugar_nacimiento"
              value={formData.lugar_nacimiento}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Dirección</label>
            <input
              type="text"
              name="direccion"
              value={formData.direccion}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Ciudad</label>
            <input
              type="text"
              name="ciudad"
              value={formData.ciudad}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Provincia</label>
            <input
              type="text"
              name="provincia"
              value={formData.provincia}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Código Postal</label>
            <input
              type="text"
              name="codigo_postal"
              value={formData.codigo_postal}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>País</label>
            <input
              type="text"
              name="pais"
              value={formData.pais}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>NIF/CIF</label>
            <input
              type="text"
              name="nif_cif"
              value={formData.nif_cif}
              onChange={handleChange}
              placeholder="Ej: B12345678"
            />
          </div>
          <div className="form-group">
            <label>Empresa</label>
            <input
              type="text"
              name="nombre_empresa"
              value={formData.nombre_empresa}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Modalidad Preferida</label>
            <select
              name="modalidad_preferida"
              value={formData.modalidad_preferida}
              onChange={handleChange}
            >
              <option value="Presencial">Presencial</option>
              <option value="Online">Online</option>
            </select>
          </div>
          <div className="form-group">
            <label>Bienvenida</label>
            <textarea
              name="bienvenida"
              value={formData.bienvenida}
              onChange={handleChange}
              rows={3}
              placeholder="Mensaje de bienvenida del cliente (rellenado desde booking)"
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>
          <div className="form-group">
            <label>Tarjeta Cliente</label>
            <input
              type="text"
              name="tarjeta_cliente"
              value={formData.tarjeta_cliente}
              onChange={handleChange}
              maxLength={25}
              placeholder="Máx. 25 caracteres"
              style={{ maxWidth: '280px' }}
            />
          </div>
          <div className="form-group">
            <label>Consideraciones Especiales</label>
            <textarea
              name="consideraciones_especiales"
              value={formData.consideraciones_especiales}
              onChange={handleChange}
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="consentimiento_comunicacion"
                checked={formData.consentimiento_comunicacion}
                onChange={handleChange}
              />
              Consentimiento Comunicación Online
            </label>
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="consentimiento_datos"
                checked={formData.consentimiento_datos}
                onChange={handleChange}
              />
              Consentimiento Tratamiento Datos
            </label>
          </div>
          <div className="form-group">
            <label>Objetivo del proceso</label>
            <Editor
              name="objetivo"
              value={formData.objetivo}
              onChange={handleChange}
              height={200}
            />
          </div>
          <div className="form-group">
            <label>Técnicas, herramientas y ejercicios utilizados</label>
            {etiquetasHerramientas.length > 0 ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>Selecciona las que apliquen (definidas en tu perfil):</p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={selectAllHerramientas}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        borderRadius: '4px',
                        border: '1px solid #28a745',
                        backgroundColor: '#fff',
                        color: '#28a745',
                        cursor: 'pointer',
                      }}
                    >
                      Seleccionar todos
                    </button>
                    <button
                      type="button"
                      onClick={clearAllHerramientas}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        borderRadius: '4px',
                        border: '1px solid #dc3545',
                        backgroundColor: '#fff',
                        color: '#dc3545',
                        cursor: 'pointer',
                      }}
                    >
                      Limpiar
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                  {etiquetasHerramientas.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => toggleHerramientaTag(e.nombre)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        border: isHerramientaSelected(e.nombre) ? '2px solid #007bff' : '1px solid #dee2e6',
                        backgroundColor: isHerramientaSelected(e.nombre) ? '#e7f1ff' : '#fff',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      {e.nombre}
                    </button>
                  ))}
                </div>
                <input type="hidden" name="herramientas" value={formData.herramientas} />
              </>
            ) : (
              <>
                <p style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>Define etiquetas en tu perfil (Configuración) para seleccionarlas aquí.</p>
                <Editor
                  name="herramientas"
                  value={formData.herramientas}
                  onChange={handleChange}
                  height={120}
                />
              </>
            )}
          </div>
          <div className="form-group">
            <label>¿Qué has aprendido de proceso de coaching?</label>
            <Editor
              name="aprendizaje"
              value={formData.aprendizaje}
              onChange={handleChange}
              height={200}
            />
          </div>
          <div className="form-group">
            <label>¿Cuáles son las áreas de mejora?</label>
            <Editor
              name="areas_mejora"
              value={formData.areas_mejora}
              onChange={handleChange}
              height={200}
            />
          </div>
          <div className="form-group">
            <label>Notas</label>
            <Editor
              name="notas"
              value={formData.notas}
              onChange={handleChange}
              height={200}
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="activo"
                checked={formData.activo}
                onChange={handleChange}
              />
              Cliente activo
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {cliente ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ClienteForm;

